import { AUTH_ROLES } from "@/constants/auth";
import { stripPhotoThumbnailsFromRecords } from "@/pages/PropertyDetail/utils/propertyPhotoRecords";

const SERVER_TEXT_SEARCH_ROLES = new Set([AUTH_ROLES.SUPERADMIN, AUTH_ROLES.REALTOR, AUTH_ROLES.WHOLESALER]);

/**
 * Roles whose Cognito groups use GSI9/GSI10 address prefix search.
 *
 * @param {unknown} role
 * @returns {boolean}
 */
export function roleUsesServerTextSearch(role) {
  const normalized = String(role ?? "")
    .trim()
    .toLowerCase();
  return SERVER_TEXT_SEARCH_ROLES.has(normalized);
}

/**
 * Cache / compare form of a list text search: trim + lowercase.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeListTextSearchQuery(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Canonical list search for cache/compare. Homeowners and other non-GSI9 roles always "".
 * Omitted `textSearchQuery` inherits `current`. Explicit `""` is the full list.
 *
 * @param {{ role?: unknown, textSearchQuery?: unknown, current?: string }} params
 * @returns {string}
 */
export function resolveListTextSearchQuery({ role, textSearchQuery, current = "" } = {}) {
  if (role != null && String(role).trim() !== "" && !roleUsesServerTextSearch(role)) {
    return "";
  }
  const incoming = textSearchQuery !== undefined ? textSearchQuery : current;
  return normalizeListTextSearchQuery(incoming);
}

/**
 * Cache key for a properties list page.
 * Role isolates GSI hats (e.g. realtor vs homeowner); search isolates server text queries.
 *
 * @param {string|null|undefined} customer
 * @param {string|null|undefined} [userId]
 * @param {string|null|undefined} [role]
 * @param {unknown} [textSearchQuery]
 * @returns {string}
 */
export function listCacheKey(customer, userId = null, role = null, textSearchQuery = "") {
  const customerPart = customer || "unknown";
  const userPart = userId == null || userId === "" ? "__none__" : String(userId).trim();
  const rolePart = role == null || role === "" ? "__none__" : String(role).trim().toLowerCase();
  const searchPart = normalizeListTextSearchQuery(textSearchQuery) || "__none__";
  return `${customerPart}::${userPart}::${rolePart}::search::${searchPart}`;
}

export function propertyKey(customer, propertyId) {
  return `${customer || "unknown"}::${propertyId || "unknown"}`;
}

/**
 * Returns true if the cached property's id matches the key's propertyId.
 * Normalizes so "123" and 123 match; "property#xyz" matches "xyz" or "property#xyz".
 * SECURITY: Used to avoid serving wrong property from cache when key/id get out of sync.
 *
 * @param {string|number|undefined|null} cachedId - id on the cached property object
 * @param {string|number|undefined|null} keyPropertyId - propertyId used in the store key
 * @returns {boolean}
 */
export function cachedPropertyIdMatchesKey(cachedId, keyPropertyId) {
  const norm = (v) => String(v ?? "").trim();
  const keyNorm = norm(keyPropertyId);
  const cachedNorm = norm(cachedId);
  if (cachedNorm === keyNorm) return true;
  if (keyNorm.startsWith("property#")) {
    const keySuffix = keyNorm.slice(9).trim();
    return keySuffix === cachedNorm || keyNorm === cachedNorm;
  }
  return false;
}

export function getCacheAge(fetchedAt) {
  return typeof fetchedAt === "number" ? Date.now() - fetchedAt : null;
}

export function isCacheFresh(cacheAgeMs, revalidateAfterMs, defaultMs) {
  const threshold = Number.isFinite(revalidateAfterMs) ? revalidateAfterMs : defaultMs;
  return cacheAgeMs !== null && cacheAgeMs < threshold;
}

export function isStatusCode(statusCode, expected) {
  return Number(statusCode) === expected;
}

export function validatePagination(pagination) {
  if (!pagination || typeof pagination !== "object") return null;
  const p = pagination;
  const validated = {
    page: typeof p.page === "number" && p.page >= 0 ? p.page : null,
    next_page: p.next_page || null,
    limit: typeof p.limit === "number" && p.limit > 0 ? p.limit : null,
    total_pages: typeof p.total_pages === "number" && p.total_pages >= 0 ? p.total_pages : null,
    has_next: typeof p.has_next === "boolean" ? p.has_next : false,
    has_previous: typeof p.has_previous === "boolean" ? p.has_previous : false,
  };
  // SECURITY: Accept pagination if either page-number or token-based cursor is present
  return validated.page !== null || validated.has_next ? validated : null;
}

export function mergePropertiesById(existing, newData) {
  const merged = [...existing];
  const idMap = new Map();
  merged.forEach((prop, idx) => {
    idMap.set(prop.id, idx);
  });

  newData.forEach((newProp) => {
    const existingIdx = idMap.get(newProp.id);
    if (existingIdx !== undefined) {
      merged[existingIdx] = newProp;
    } else {
      merged.push(newProp);
      idMap.set(newProp.id, merged.length - 1);
    }
  });

  return merged;
}

export function mergePropertyPatch(current, partialUpdate) {
  const source = current && typeof current === "object" ? current : {};
  const patch = partialUpdate && typeof partialUpdate === "object" ? partialUpdate : {};
  const merged = { ...source };

  if (patch.additional_attributes && typeof patch.additional_attributes === "object") {
    merged.additional_attributes = {
      ...(source.additional_attributes || {}),
      ...patch.additional_attributes,
    };
  }

  Object.keys(patch).forEach((key) => {
    if (key === "additional_attributes") return;
    if (Object.prototype.hasOwnProperty.call(patch, key)) {
      merged[key] = patch[key];
    }
  });

  if (Array.isArray(merged.additional_attributes?.photos)) {
    merged.additional_attributes = {
      ...merged.additional_attributes,
      photos: stripPhotoThumbnailsFromRecords(merged.additional_attributes.photos),
    };
  }

  return merged;
}
